import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  Stack,
  IconButton,
  Tooltip,
  Button,
  Divider,
  Avatar,
  useTheme,
  alpha,
  Skeleton,
  Fade,
  Zoom,
  Grow
} from '@mui/material';
import {
  Security,
  Block,
  Warning,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Science as ScienceIcon,
  Settings as SettingsIcon,
  Analytics as AnalyticsIcon,
  Shield,
  Gavel,
  ArrowForward,
  Timeline,
  Speed,
  VerifiedUser,
  Assessment,
  AutoAwesome,
  Insights,
  LocalFireDepartment
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import NeuraStrikeLogo from '../components/NeuraStrikeLogo';

interface DashboardStats {
  summary: {
    total_prompts: number;
    blocked_prompts: number;
    flagged_prompts: number;
    allowed_prompts: number;
    block_rate: string;
    avg_threat_score: number;
    total_tokens_used: number;
    active_security_rules: number;
    total_security_rules: number;
  };
  recent_threats: Array<{
    id: number;
    prompt_preview: string;
    threat_score: number;
    threat_types: string[];
    created_at: string;
  }>;
}

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboardStats();
    }
  }, [isAuthenticated]);

  const fetchDashboardStats = async () => {
    try {
      setRefreshing(true);
      const response = await axios.get('/api/dashboard/stats');
      setStats(response.data);
      setError(null);
    } catch (err: any) {
      setError('Failed to load dashboard data');
      console.error('Dashboard error:', err);
      // Set default data for MVP
      setStats({
        summary: {
          total_prompts: 0,
          blocked_prompts: 0,
          flagged_prompts: 0,
          allowed_prompts: 0,
          block_rate: '0.0%',
          avg_threat_score: 0.0,
          total_tokens_used: 0,
          active_security_rules: 0,
          total_security_rules: 0
        },
        recent_threats: []
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getThreatColor = (score: number) => {
    if (score >= 0.8) return 'error';
    if (score >= 0.5) return 'warning';
    return 'success';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Header Skeleton */}
        <Box sx={{ mb: 4 }}>
          <Skeleton
            variant="rectangular"
            height={120}
            sx={{
              borderRadius: 4,
              mb: 2,
              animation: 'pulse 1.5s ease-in-out infinite'
            }}
          />
        </Box>

        {/* Stats Cards Skeleton */}
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} lg={3} key={i}>
              <Skeleton
                variant="rectangular"
                height={180}
                sx={{
                  borderRadius: 3,
                  animation: `pulse 1.5s ease-in-out ${i * 0.1}s infinite`
                }}
              />
            </Grid>
          ))}

          {/* Additional Skeletons */}
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} md={6} lg={4} key={`skeleton-${i}`}>
              <Skeleton
                variant="rectangular"
                height={380}
                sx={{
                  borderRadius: 3,
                  animation: `pulse 1.5s ease-in-out ${i * 0.15}s infinite`
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '1600px', margin: '0 auto' }}>
      {/* Enhanced Header Section with Welcome Message */}
      <Fade in timeout={600}>
        <Paper
          elevation={0}
          sx={{
            mb: 4,
            p: { xs: 2.5, sm: 4 },
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            position: 'relative',
            overflow: 'hidden',
            backdropFilter: 'blur(20px)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '400px',
              height: '400px',
              background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.15)} 0%, transparent 70%)`,
              borderRadius: '50%',
              transform: 'translate(30%, -30%)',
              animation: 'pulse 4s ease-in-out infinite'
            },
            '@keyframes pulse': {
              '0%, 100%': { opacity: 0.6 },
              '50%': { opacity: 1 }
            }
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                <Zoom in timeout={800}>
                  <Box>
                    <NeuraStrikeLogo height={40} />
                  </Box>
                </Zoom>

                <Box>
                  <Typography
                    variant="h4"
                    component="h1"
                    sx={{
                      fontWeight: 800,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      letterSpacing: '-0.025em',
                      mb: 0.5,
                      fontSize: { xs: '1.75rem', sm: '2.125rem' }
                    }}
                  >
                    Welcome back{user?.email ? `, ${user.email.split('@')[0]}` : ''}!
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: { xs: '0.813rem', sm: '0.875rem' }
                    }}
                  >
                    <VerifiedUser sx={{ fontSize: 18 }} />
                    Real-time security monitoring and analytics
                  </Typography>
                </Box>
              </Box>

              <Stack direction="row" spacing={1.5}>
                <Tooltip title="Refresh dashboard data" arrow placement="bottom">
                  <IconButton
                    onClick={fetchDashboardStats}
                    disabled={refreshing}
                    sx={{
                      background: alpha(theme.palette.primary.main, 0.12),
                      backdropFilter: 'blur(10px)',
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                      width: 44,
                      height: 44,
                      '&:hover': {
                        background: alpha(theme.palette.primary.main, 0.2),
                        transform: 'rotate(180deg)',
                        transition: 'transform 0.6s ease'
                      },
                      '&:disabled': {
                        background: alpha(theme.palette.action.disabled, 0.1)
                      }
                    }}
                  >
                    {refreshing ? (
                      <CircularProgress
                        size={20}
                        thickness={4}
                        sx={{ color: theme.palette.primary.main }}
                      />
                    ) : (
                      <RefreshIcon sx={{ color: theme.palette.primary.main }} />
                    )}
                  </IconButton>
                </Tooltip>

                <Button
                  component={Link}
                  to="/prompt-tester"
                  variant="contained"
                  startIcon={<ScienceIcon />}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
                    px: 3,
                    py: 1.25,
                    fontWeight: 700,
                    fontSize: '0.938rem',
                    textTransform: 'none',
                    borderRadius: 2.5,
                    '&:hover': {
                      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                      transform: 'translateY(-2px)',
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  Test Prompt
                </Button>
              </Stack>
            </Box>
          </Box>
        </Paper>
      </Fade>

      {error && (
        <Fade in timeout={400}>
          <Alert
            severity="info"
            sx={{
              mb: 3,
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
              background: alpha(theme.palette.info.main, 0.08),
              backdropFilter: 'blur(10px)',
              '& .MuiAlert-icon': {
                color: theme.palette.info.main
              },
              '& .MuiAlert-message': {
                fontWeight: 500
              }
            }}
            icon={<InfoIcon />}
          >
            {error} - Showing demo data for MVP
          </Alert>
        </Fade>
      )}

      {/* Key Performance Indicators */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Total Prompts Card */}
        <Grid item xs={12} sm={6} lg={3}>
          <Grow in timeout={600}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha(theme.palette.primary.light, 0.08)} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-12px) scale(1.02)',
                  boxShadow: `0 16px 32px ${alpha(theme.palette.primary.main, 0.2)}`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                  '& .stat-icon': {
                    transform: 'scale(1.1) rotate(5deg)'
                  },
                  '& .stat-number': {
                    transform: 'scale(1.05)'
                  }
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '120px',
                  height: '120px',
                  background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.15)} 0%, transparent 70%)`,
                  borderRadius: '50%',
                  transform: 'translate(30%, -30%)'
                }
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Stack spacing={2.5}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Avatar
                      className="stat-icon"
                      sx={{
                        width: 60,
                        height: 60,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.25)} 100%)`,
                        border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                        transition: 'transform 0.3s ease'
                      }}
                    >
                      <Security sx={{ fontSize: 30, color: theme.palette.primary.main }} />
                    </Avatar>
                    <Chip
                      label="All Time"
                      size="small"
                      sx={{
                        background: alpha(theme.palette.primary.main, 0.12),
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                        fontSize: '0.688rem',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                        height: 26
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontWeight: 700,
                        mb: 1.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.8px',
                        fontSize: '0.75rem'
                      }}
                    >
                      Total Prompts
                    </Typography>
                    <Typography
                      className="stat-number"
                      variant="h3"
                      component="h2"
                      sx={{
                        fontWeight: 800,
                        color: theme.palette.primary.main,
                        letterSpacing: '-0.025em',
                        mb: 1,
                        transition: 'transform 0.3s ease',
                        fontSize: { xs: '2rem', sm: '2.5rem' }
                      }}
                    >
                      {stats?.summary.total_prompts.toLocaleString() || 0}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <Timeline sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600, fontSize: '0.813rem' }}
                      >
                        Lifetime activity
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grow>
        </Grid>

        {/* Blocked Prompts Card */}
        <Grid item xs={12} sm={6} lg={3}>
          <Grow in timeout={700}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha(theme.palette.error.light, 0.08)} 100%)`,
                border: `1px solid ${alpha(theme.palette.error.main, 0.15)}`,
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-12px) scale(1.02)',
                  boxShadow: `0 16px 32px ${alpha(theme.palette.error.main, 0.2)}`,
                  border: `1px solid ${alpha(theme.palette.error.main, 0.4)}`,
                  '& .stat-icon': {
                    transform: 'scale(1.1) rotate(-5deg)'
                  },
                  '& .stat-number': {
                    transform: 'scale(1.05)'
                  }
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '120px',
                  height: '120px',
                  background: `radial-gradient(circle, ${alpha(theme.palette.error.main, 0.15)} 0%, transparent 70%)`,
                  borderRadius: '50%',
                  transform: 'translate(30%, -30%)'
                }
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Stack spacing={2.5}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Avatar
                      className="stat-icon"
                      sx={{
                        width: 60,
                        height: 60,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.15)} 0%, ${alpha(theme.palette.error.main, 0.25)} 100%)`,
                        border: `2px solid ${alpha(theme.palette.error.main, 0.3)}`,
                        transition: 'transform 0.3s ease'
                      }}
                    >
                      <Block sx={{ fontSize: 30, color: theme.palette.error.main }} />
                    </Avatar>
                    <Chip
                      icon={<TrendingDown sx={{ fontSize: 16 }} />}
                      label="Blocked"
                      size="small"
                      sx={{
                        background: alpha(theme.palette.error.main, 0.12),
                        color: theme.palette.error.main,
                        fontWeight: 700,
                        fontSize: '0.688rem',
                        border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
                        height: 26
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontWeight: 700,
                        mb: 1.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.8px',
                        fontSize: '0.75rem'
                      }}
                    >
                      Blocked Prompts
                    </Typography>
                    <Typography
                      className="stat-number"
                      variant="h3"
                      component="h2"
                      sx={{
                        fontWeight: 800,
                        color: theme.palette.error.main,
                        letterSpacing: '-0.025em',
                        mb: 1,
                        transition: 'transform 0.3s ease',
                        fontSize: { xs: '2rem', sm: '2.5rem' }
                      }}
                    >
                      {stats?.summary.blocked_prompts.toLocaleString() || 0}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <Shield sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600, fontSize: '0.813rem' }}
                      >
                        Threats prevented
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grow>
        </Grid>

        {/* Flagged Prompts Card */}
        <Grid item xs={12} sm={6} lg={3}>
          <Grow in timeout={800}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha(theme.palette.warning.light, 0.08)} 100%)`,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.15)}`,
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-12px) scale(1.02)',
                  boxShadow: `0 16px 32px ${alpha(theme.palette.warning.main, 0.2)}`,
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
                  '& .stat-icon': {
                    transform: 'scale(1.1) rotate(10deg)'
                  },
                  '& .stat-number': {
                    transform: 'scale(1.05)'
                  }
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '120px',
                  height: '120px',
                  background: `radial-gradient(circle, ${alpha(theme.palette.warning.main, 0.15)} 0%, transparent 70%)`,
                  borderRadius: '50%',
                  transform: 'translate(30%, -30%)'
                }
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Stack spacing={2.5}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Avatar
                      className="stat-icon"
                      sx={{
                        width: 60,
                        height: 60,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.15)} 0%, ${alpha(theme.palette.warning.main, 0.25)} 100%)`,
                        border: `2px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                        transition: 'transform 0.3s ease'
                      }}
                    >
                      <Warning sx={{ fontSize: 30, color: theme.palette.warning.main }} />
                    </Avatar>
                    <Chip
                      icon={<Speed sx={{ fontSize: 16 }} />}
                      label="Review"
                      size="small"
                      sx={{
                        background: alpha(theme.palette.warning.main, 0.12),
                        color: theme.palette.warning.main,
                        fontWeight: 700,
                        fontSize: '0.688rem',
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
                        height: 26
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontWeight: 700,
                        mb: 1.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.8px',
                        fontSize: '0.75rem'
                      }}
                    >
                      Flagged Prompts
                    </Typography>
                    <Typography
                      className="stat-number"
                      variant="h3"
                      component="h2"
                      sx={{
                        fontWeight: 800,
                        color: theme.palette.warning.main,
                        letterSpacing: '-0.025em',
                        mb: 1,
                        transition: 'transform 0.3s ease',
                        fontSize: { xs: '2rem', sm: '2.5rem' }
                      }}
                    >
                      {stats?.summary.flagged_prompts.toLocaleString() || 0}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <Assessment sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600, fontSize: '0.813rem' }}
                      >
                        Requires attention
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grow>
        </Grid>

        {/* Allowed Prompts Card */}
        <Grid item xs={12} sm={6} lg={3}>
          <Grow in timeout={900}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha(theme.palette.success.light, 0.08)} 100%)`,
                border: `1px solid ${alpha(theme.palette.success.main, 0.15)}`,
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-12px) scale(1.02)',
                  boxShadow: `0 16px 32px ${alpha(theme.palette.success.main, 0.2)}`,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.4)}`,
                  '& .stat-icon': {
                    transform: 'scale(1.1) rotate(-10deg)'
                  },
                  '& .stat-number': {
                    transform: 'scale(1.05)'
                  }
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '120px',
                  height: '120px',
                  background: `radial-gradient(circle, ${alpha(theme.palette.success.main, 0.15)} 0%, transparent 70%)`,
                  borderRadius: '50%',
                  transform: 'translate(30%, -30%)'
                }
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Stack spacing={2.5}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Avatar
                      className="stat-icon"
                      sx={{
                        width: 60,
                        height: 60,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.15)} 0%, ${alpha(theme.palette.success.main, 0.25)} 100%)`,
                        border: `2px solid ${alpha(theme.palette.success.main, 0.3)}`,
                        transition: 'transform 0.3s ease'
                      }}
                    >
                      <CheckCircle sx={{ fontSize: 30, color: theme.palette.success.main }} />
                    </Avatar>
                    <Chip
                      icon={<TrendingUp sx={{ fontSize: 16 }} />}
                      label="Safe"
                      size="small"
                      sx={{
                        background: alpha(theme.palette.success.main, 0.12),
                        color: theme.palette.success.main,
                        fontWeight: 700,
                        fontSize: '0.688rem',
                        border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
                        height: 26
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontWeight: 700,
                        mb: 1.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.8px',
                        fontSize: '0.75rem'
                      }}
                    >
                      Allowed Prompts
                    </Typography>
                    <Typography
                      className="stat-number"
                      variant="h3"
                      component="h2"
                      sx={{
                        fontWeight: 800,
                        color: theme.palette.success.main,
                        letterSpacing: '-0.025em',
                        mb: 1,
                        transition: 'transform 0.3s ease',
                        fontSize: { xs: '2rem', sm: '2.5rem' }
                      }}
                    >
                      {stats?.summary.allowed_prompts.toLocaleString() || 0}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <CheckCircle sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600, fontSize: '0.813rem' }}
                      >
                        Verified safe
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grow>
        </Grid>
      </Grid>

      {/* Secondary Metrics Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Compliance Security Rules Widget */}
        <Grid item xs={12} md={6} lg={4}>
          <Card
            elevation={0}
            sx={{
              height: '100%',
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha('#9333ea', 0.05)} 100%)`,
              border: `1px solid ${alpha('#9333ea', 0.15)}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 24px ${alpha('#9333ea', 0.2)}`
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={3}>
                {/* Header */}
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        background: `linear-gradient(135deg, ${alpha('#9333ea', 0.1)} 0%, ${alpha('#9333ea', 0.2)} 100%)`,
                        border: `2px solid ${alpha('#9333ea', 0.2)}`
                      }}
                    >
                      <Shield sx={{ fontSize: 24, color: '#9333ea' }} />
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color: '#9333ea', lineHeight: 1.2 }}
                      >
                        Security Rules
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Active protection
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label="Active"
                    size="small"
                    sx={{
                      background: alpha('#9333ea', 0.1),
                      color: '#9333ea',
                      fontWeight: 700,
                      fontSize: '0.688rem',
                      border: `1px solid ${alpha('#9333ea', 0.2)}`
                    }}
                  />
                </Box>

                <Divider />

                {/* Stats */}
                <Box>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 800,
                      color: '#9333ea',
                      letterSpacing: '-0.025em',
                      mb: 0.5
                    }}
                  >
                    {stats?.summary.total_security_rules ?? 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {stats?.summary.active_security_rules ?? 0} active ·{' '}
                    {(stats?.summary.total_security_rules ?? 0) -
                      (stats?.summary.active_security_rules ?? 0)}{' '}
                    inactive
                  </Typography>
                </Box>

                {/* Action Button */}
                <Button
                  component={Link}
                  to="/security-rules"
                  variant="outlined"
                  endIcon={<ArrowForward />}
                  fullWidth
                  sx={{
                    mt: 1,
                    borderColor: alpha('#9333ea', 0.3),
                    color: '#9333ea',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#9333ea',
                      background: alpha('#9333ea', 0.05)
                    }
                  }}
                >
                  View All Rules
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Security Metrics */}
        <Grid item xs={12} md={6} lg={4}>
          <Card
            elevation={0}
            sx={{
              height: '100%',
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[4]
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.2)} 100%)`,
                    border: `2px solid ${alpha(theme.palette.info.main, 0.2)}`
                  }}
                >
                  <AnalyticsIcon sx={{ fontSize: 24, color: theme.palette.info.main }} />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    Security Metrics
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Performance overview
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Stack spacing={3}>
                {/* Block Rate */}
                <Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: theme.palette.text.secondary }}
                    >
                      Block Rate
                    </Typography>
                    <Chip
                      label={stats?.summary.block_rate || '0.0%'}
                      size="small"
                      sx={{
                        background:
                          parseFloat(stats?.summary.block_rate || '0') > 10
                            ? alpha(theme.palette.error.main, 0.1)
                            : alpha(theme.palette.success.main, 0.1),
                        color:
                          parseFloat(stats?.summary.block_rate || '0') > 10
                            ? theme.palette.error.main
                            : theme.palette.success.main,
                        fontWeight: 700,
                        fontSize: '0.813rem',
                        border: `1px solid`,
                        borderColor:
                          parseFloat(stats?.summary.block_rate || '0') > 10
                            ? alpha(theme.palette.error.main, 0.3)
                            : alpha(theme.palette.success.main, 0.3)
                      }}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(parseFloat(stats?.summary.block_rate || '0'), 100)}
                    sx={{
                      height: 10,
                      borderRadius: 10,
                      backgroundColor: alpha(theme.palette.divider, 0.3),
                      '& .MuiLinearProgress-bar': {
                        background:
                          parseFloat(stats?.summary.block_rate || '0') > 10
                            ? `linear-gradient(90deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`
                            : `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                        borderRadius: 10
                      }
                    }}
                  />
                </Box>

                {/* Average Threat Score */}
                <Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: theme.palette.text.secondary }}
                    >
                      Avg Threat Score
                    </Typography>
                    <Chip
                      label={(Number(stats?.summary.avg_threat_score) || 0).toFixed(2)}
                      size="small"
                      sx={{
                        background:
                          getThreatColor(Number(stats?.summary.avg_threat_score) || 0) === 'error'
                            ? alpha(theme.palette.error.main, 0.1)
                            : getThreatColor(Number(stats?.summary.avg_threat_score) || 0) ===
                                'warning'
                              ? alpha(theme.palette.warning.main, 0.1)
                              : alpha(theme.palette.success.main, 0.1),
                        color:
                          getThreatColor(Number(stats?.summary.avg_threat_score) || 0) === 'error'
                            ? theme.palette.error.main
                            : getThreatColor(Number(stats?.summary.avg_threat_score) || 0) ===
                                'warning'
                              ? theme.palette.warning.main
                              : theme.palette.success.main,
                        fontWeight: 700,
                        fontSize: '0.813rem',
                        border: `1px solid`,
                        borderColor:
                          getThreatColor(Number(stats?.summary.avg_threat_score) || 0) === 'error'
                            ? alpha(theme.palette.error.main, 0.3)
                            : getThreatColor(Number(stats?.summary.avg_threat_score) || 0) ===
                                'warning'
                              ? alpha(theme.palette.warning.main, 0.3)
                              : alpha(theme.palette.success.main, 0.3)
                      }}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(Number(stats?.summary.avg_threat_score) || 0) * 100}
                    sx={{
                      height: 10,
                      borderRadius: 10,
                      backgroundColor: alpha(theme.palette.divider, 0.3),
                      '& .MuiLinearProgress-bar': {
                        background:
                          getThreatColor(Number(stats?.summary.avg_threat_score) || 0) === 'error'
                            ? `linear-gradient(90deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`
                            : getThreatColor(Number(stats?.summary.avg_threat_score) || 0) ===
                                'warning'
                              ? `linear-gradient(90deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`
                              : `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                        borderRadius: 10
                      }
                    }}
                  />
                </Box>

                {/* Total Tokens */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    background: alpha(theme.palette.primary.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: theme.palette.text.secondary, mb: 0.5 }}
                      >
                        Total Tokens Used
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 800,
                          color: theme.palette.primary.main,
                          letterSpacing: '-0.025em'
                        }}
                      >
                        {(stats?.summary.total_tokens_used || 0).toLocaleString()}
                      </Typography>
                    </Box>
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        background: alpha(theme.palette.primary.main, 0.1)
                      }}
                    >
                      <Speed sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                    </Avatar>
                  </Box>
                </Paper>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Threats */}
        <Grid item xs={12} md={6} lg={4}>
          <Card
            elevation={0}
            sx={{
              height: '100%',
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[4]
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.2)} 100%)`,
                      border: `2px solid ${alpha(theme.palette.error.main, 0.2)}`
                    }}
                  >
                    <Warning sx={{ fontSize: 24, color: theme.palette.error.main }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      Recent Threats
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Latest detections
                    </Typography>
                  </Box>
                </Box>
                <Tooltip title="View all threats" arrow>
                  <IconButton
                    component={Link}
                    to="/prompt-history"
                    size="small"
                    sx={{
                      background: alpha(theme.palette.error.main, 0.1),
                      '&:hover': {
                        background: alpha(theme.palette.error.main, 0.2)
                      }
                    }}
                  >
                    <ArrowForward sx={{ fontSize: 18, color: theme.palette.error.main }} />
                  </IconButton>
                </Tooltip>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {stats?.recent_threats && stats.recent_threats.length > 0 ? (
                <List sx={{ p: 0, maxHeight: 320, overflow: 'auto' }}>
                  {stats.recent_threats.slice(0, 5).map((threat, index) => (
                    <ListItem
                      key={threat.id}
                      divider={index < 4}
                      sx={{
                        px: 0,
                        py: 2,
                        borderRadius: 2,
                        '&:hover': {
                          background: alpha(theme.palette.error.main, 0.03),
                          px: 1.5
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={1}
                            gap={1}
                          >
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{
                                flex: 1,
                                fontWeight: 600,
                                color: theme.palette.text.primary
                              }}
                            >
                              {threat.prompt_preview}
                            </Typography>
                            <Chip
                              label={`${(Number(threat.threat_score) * 100 || 0).toFixed(0)}%`}
                              size="small"
                              sx={{
                                background:
                                  getThreatColor(Number(threat.threat_score) || 0) === 'error'
                                    ? alpha(theme.palette.error.main, 0.1)
                                    : getThreatColor(Number(threat.threat_score) || 0) === 'warning'
                                      ? alpha(theme.palette.warning.main, 0.1)
                                      : alpha(theme.palette.success.main, 0.1),
                                color:
                                  getThreatColor(Number(threat.threat_score) || 0) === 'error'
                                    ? theme.palette.error.main
                                    : getThreatColor(Number(threat.threat_score) || 0) === 'warning'
                                      ? theme.palette.warning.main
                                      : theme.palette.success.main,
                                fontWeight: 700,
                                fontSize: '0.75rem',
                                border: `1px solid`,
                                borderColor:
                                  getThreatColor(Number(threat.threat_score) || 0) === 'error'
                                    ? alpha(theme.palette.error.main, 0.3)
                                    : getThreatColor(Number(threat.threat_score) || 0) === 'warning'
                                      ? alpha(theme.palette.warning.main, 0.3)
                                      : alpha(theme.palette.success.main, 0.3),
                                minWidth: 50
                              }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Box display="flex" flexWrap="wrap" gap={0.5} mb={0.75}>
                              {threat.threat_types.slice(0, 3).map((type) => (
                                <Chip
                                  key={type}
                                  label={type}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontSize: '0.688rem',
                                    height: 22,
                                    borderColor: alpha(theme.palette.divider, 0.5),
                                    color: theme.palette.text.secondary,
                                    fontWeight: 600
                                  }}
                                />
                              ))}
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.palette.text.secondary,
                                fontSize: '0.75rem',
                                fontWeight: 500
                              }}
                            >
                              {formatDate(threat.created_at)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    textAlign: 'center',
                    py: 5,
                    px: 2,
                    borderRadius: 3,
                    background: alpha(theme.palette.success.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                  }}
                >
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      margin: '0 auto',
                      mb: 2,
                      background: alpha(theme.palette.success.main, 0.1)
                    }}
                  >
                    <CheckCircle sx={{ fontSize: 32, color: theme.palette.success.main }} />
                  </Avatar>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.success.dark,
                      mb: 0.5
                    }}
                  >
                    All Clear!
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.success.main }}>
                    No recent threats detected
                  </Typography>
                </Paper>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Start Guide - Enhanced */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 4,
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha(theme.palette.background.default, 0.5)} 100%)`,
          border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 70%)`,
            borderRadius: '50%'
          }
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box display="flex" alignItems="center" gap={2} mb={1}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
              }}
            >
              <InfoIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  letterSpacing: '-0.025em',
                  lineHeight: 1.2
                }}
              >
                Quick Start Guide
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Get started with NeuraStrike in three simple steps
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Grid container spacing={3}>
            {/* Step 1 */}
            <Grid item xs={12} sm={4}>
              <Card
                elevation={0}
                component={Link}
                to="/prompt-tester"
                sx={{
                  height: '100%',
                  textDecoration: 'none',
                  borderRadius: 3,
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, ${alpha('#ffffff', 1)} 100%)`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    transform: 'translateY(-8px)',
                    boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.25)}`,
                    '& .step-number': {
                      transform: 'scale(1.1) rotate(5deg)'
                    },
                    '& .arrow-icon': {
                      transform: 'translateX(4px)'
                    }
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '150px',
                    height: '150px',
                    background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
                    borderRadius: '50%',
                    transform: 'translate(40%, -40%)'
                  }
                }}
              >
                <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                  <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
                    <Avatar
                      className="step-number"
                      sx={{
                        width: 56,
                        height: 56,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                        transition: 'transform 0.3s ease'
                      }}
                    >
                      <ScienceIcon sx={{ fontSize: 28, color: '#ffffff' }} />
                    </Avatar>
                    <Chip
                      label="Step 1"
                      size="small"
                      sx={{
                        background: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                        fontSize: '0.688rem',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      mb: 1.5,
                      letterSpacing: '-0.025em'
                    }}
                  >
                    Test Prompts
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      lineHeight: 1.7,
                      mb: 2
                    }}
                  >
                    Test your prompts for security threats and see real-time analysis with detailed
                    threat scoring.
                  </Typography>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                    sx={{ color: theme.palette.primary.main }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Get Started
                    </Typography>
                    <ArrowForward
                      className="arrow-icon"
                      sx={{ fontSize: 16, transition: 'transform 0.3s ease' }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Step 2 */}
            <Grid item xs={12} sm={4}>
              <Card
                elevation={0}
                component={Link}
                to="/security-rules"
                sx={{
                  height: '100%',
                  textDecoration: 'none',
                  borderRadius: 3,
                  border: `2px solid ${alpha(theme.palette.secondary.main, 0.15)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.03)} 0%, ${alpha('#ffffff', 1)} 100%)`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    borderColor: theme.palette.secondary.main,
                    transform: 'translateY(-8px)',
                    boxShadow: `0 12px 24px ${alpha(theme.palette.secondary.main, 0.25)}`,
                    '& .step-number': {
                      transform: 'scale(1.1) rotate(5deg)'
                    },
                    '& .arrow-icon': {
                      transform: 'translateX(4px)'
                    }
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '150px',
                    height: '150px',
                    background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.1)} 0%, transparent 70%)`,
                    borderRadius: '50%',
                    transform: 'translate(40%, -40%)'
                  }
                }}
              >
                <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                  <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
                    <Avatar
                      className="step-number"
                      sx={{
                        width: 56,
                        height: 56,
                        background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.3)}`,
                        transition: 'transform 0.3s ease'
                      }}
                    >
                      <SettingsIcon sx={{ fontSize: 28, color: '#ffffff' }} />
                    </Avatar>
                    <Chip
                      label="Step 2"
                      size="small"
                      sx={{
                        background: alpha(theme.palette.secondary.main, 0.1),
                        color: theme.palette.secondary.main,
                        fontWeight: 700,
                        fontSize: '0.688rem',
                        border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.secondary.main,
                      mb: 1.5,
                      letterSpacing: '-0.025em'
                    }}
                  >
                    Configure Rules
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      lineHeight: 1.7,
                      mb: 2
                    }}
                  >
                    Customize threat detection patterns and security policies to match your
                    requirements.
                  </Typography>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                    sx={{ color: theme.palette.secondary.main }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Configure Now
                    </Typography>
                    <ArrowForward
                      className="arrow-icon"
                      sx={{ fontSize: 16, transition: 'transform 0.3s ease' }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Step 3 */}
            <Grid item xs={12} sm={4}>
              <Card
                elevation={0}
                component={Link}
                to="/prompt-history"
                sx={{
                  height: '100%',
                  textDecoration: 'none',
                  borderRadius: 3,
                  border: `2px solid ${alpha(theme.palette.info.main, 0.15)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.03)} 0%, ${alpha('#ffffff', 1)} 100%)`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    borderColor: theme.palette.info.main,
                    transform: 'translateY(-8px)',
                    boxShadow: `0 12px 24px ${alpha(theme.palette.info.main, 0.25)}`,
                    '& .step-number': {
                      transform: 'scale(1.1) rotate(5deg)'
                    },
                    '& .arrow-icon': {
                      transform: 'translateX(4px)'
                    }
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '150px',
                    height: '150px',
                    background: `radial-gradient(circle, ${alpha(theme.palette.info.main, 0.1)} 0%, transparent 70%)`,
                    borderRadius: '50%',
                    transform: 'translate(40%, -40%)'
                  }
                }}
              >
                <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                  <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
                    <Avatar
                      className="step-number"
                      sx={{
                        width: 56,
                        height: 56,
                        background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.3)}`,
                        transition: 'transform 0.3s ease'
                      }}
                    >
                      <AnalyticsIcon sx={{ fontSize: 28, color: '#ffffff' }} />
                    </Avatar>
                    <Chip
                      label="Step 3"
                      size="small"
                      sx={{
                        background: alpha(theme.palette.info.main, 0.1),
                        color: theme.palette.info.main,
                        fontWeight: 700,
                        fontSize: '0.688rem',
                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.info.main,
                      mb: 1.5,
                      letterSpacing: '-0.025em'
                    }}
                  >
                    Monitor Activity
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      lineHeight: 1.7,
                      mb: 2
                    }}
                  >
                    Review all processed prompts and their security analysis with comprehensive
                    logs.
                  </Typography>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                    sx={{ color: theme.palette.info.main }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      View History
                    </Typography>
                    <ArrowForward
                      className="arrow-icon"
                      sx={{ fontSize: 16, transition: 'transform 0.3s ease' }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default Dashboard;
